'use client';

import React, { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetPayerNameSuggestionsInfiniteQuery } from 'src/api/redux';
// Import directly from hook file to avoid circular dependency via hooks/index.ts
import { useGenericComponent } from '../../hooks/useGenericComponent';
import { GenericSelectorModal, GenericNodeComponent, ConnectorType } from '../shared';
import {
  PayerNameItem,
  payerNameModalConfig,
  payerNameNodeConfig,
  transformPayerName,
} from './config';

interface PayerNameComponentProps {
  componentId: string;
  initialPayerNames?: PayerNameItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

// Extract IDs for Redux storage
const getItemIds = (items: PayerNameItem[]) => items.map((p) => p.id);

const PayerNameComponent: React.FC<PayerNameComponentProps> = ({
  componentId,
  initialPayerNames = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Use generic component hook for state management
  const {
    items: payerNames,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<PayerNameItem>({
    componentId,
    type: 'payer_name',
    reduxDataField: 'value',
    getItemIds,
    initialItems: initialPayerNames,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Debounce search term
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch search results
  const { data, isLoading } = useGetPayerNameSuggestionsInfiniteQuery({
    q: debouncedSearch,
  });

  // Transform API results to standard format
  const searchResults: PayerNameItem[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results ?? []).map(transformPayerName);
  }, [data]);

  return (
    <>
      {payerNames.length > 0 ? (
        <GenericNodeComponent
          config={payerNameNodeConfig}
          items={payerNames}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-sky-50 border border-sky-200 rounded-full cursor-pointer hover:bg-sky-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-sky-600 text-xs font-medium">
            Select payer names...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={payerNameModalConfig}
        isOpen={showModal}
        selectedItems={payerNames}
        connector={connector}
        isEditing={payerNames.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default PayerNameComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderPayerNameComponent = (
  container: HTMLElement,
  props: PayerNameComponentProps & {
    initialPayerNames?: PayerNameItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <PayerNameComponent {...props} />
    </Provider>
  );
  return root;
};
